// Faction Standing (design doc 16.48, LOCKED, extended by 16.55).  Every
// captain tracks four independent standing values -- one per real faction
// plus Pirates -- on a -10..+10 scale, starting at 0.
import type { StarSystem } from "./types";

export type FactionName = "Crimson Concord" | "Azure Compact" | "Solthane Accord" | "Pirate";
export const FACTION_NAMES: FactionName[] = ["Crimson Concord", "Azure Compact", "Solthane Accord", "Pirate"];

export type FactionStanding = Record<FactionName, number>;

export function initialStanding(): FactionStanding {
  return { "Crimson Concord": 0, "Azure Compact": 0, "Solthane Accord": 0, Pirate: 0 };
}

export type StandingTier = "Hostile" | "Neutral" | "Friendly";

// Three tiers on a -10..+10 scale. 16.93 widened the thresholds from +/-4 to
// +/-5 per Don's direction ("the middle range of +5 to -5 being neutral"):
// Hostile -10..-5, Neutral -4..+4 (starting band), Friendly +5..+10.
//
// That phrasing is ambiguous between "the band [-5, +5] is Neutral" (making
// Friendly start at +6) and "+/-5 are the thresholds" (Friendly at +5), and the
// choice matters because it changes how long alignment takes to earn. Taken as
// thresholds, because it reads the numbers he actually named, keeps the three
// bands near-equal thirds of the scale, and is the smaller move from the
// existing +/-4. Flipping to the other reading is a change to these two
// constants and nothing else.
//
// Both are named rather than inlined because 16.84 derives the per-mission
// standing gain from FRIENDLY_MIN_STANDING instead of restating a number. The
// happy consequence is that widening the threshold does NOT re-pace the climb:
// the gain moves 0.4 -> 0.5 with it, so Friendly still lands on the tenth
// mission exactly as designed, and 5/10 terminates in binary just as 4/10 did.
//
// One real balance consequence though, since the pirate penalty is a flat -1.0
// and did NOT scale: a pirate job used to cost 2.5 legitimate missions of
// real-faction standing to repay, and now costs 2.0. Pirate work got slightly
// cheaper to recover from. Recorded rather than silently absorbed -- if the
// 2.5 ratio was load-bearing, the penalty is what should move, not this.
export const FRIENDLY_MIN_STANDING = 5;
export const HOSTILE_MAX_STANDING = -5;

export function standingTier(value: number): StandingTier {
  if (value <= HOSTILE_MAX_STANDING) return "Hostile";
  if (value >= FRIENDLY_MIN_STANDING) return "Friendly";
  return "Neutral";
}

const MIN_STANDING = -10;
const MAX_STANDING = 10;

export function clampStanding(value: number): number {
  return Math.max(MIN_STANDING, Math.min(MAX_STANDING, value));
}

// Rounded to 4 decimals on every adjustment (16.84). Repeated addition of even
// a clean 0.4 drifts in binary floating point (ten of them sum to
// 3.9999999999999996), and whether a captain actually crosses into Friendly on
// their tenth mission must not depend on that. Rounding each running total to a
// granularity the gain divides evenly into makes every crossing exact. 4
// decimals is far finer than anything displayed (the Space Map shows one) and
// leaves the exact -1.0 penalties untouched.
function roundStanding(value: number): number {
  return Math.round(value * 1e4) / 1e4;
}

export function adjustStanding(standing: FactionStanding, faction: FactionName, delta: number): FactionStanding {
  return { ...standing, [faction]: clampStanding(roundStanding(standing[faction] + delta)) };
}

// A mission's "poster" is whichever real faction/Pirates controls the
// System its Mission Board was generated for (Section 6's region model) --
// "safe"/"neutral" systems post no faction-tagged missions, so completing
// or abandoning one there never touches standing with anyone.
export function posterFactionForSystem(system: StarSystem): FactionName | null {
  switch (system.region) {
    case "crimson":
      return "Crimson Concord";
    case "azure":
      return "Azure Compact";
    case "solthane":
      return "Solthane Accord";
    case "pirate":
      return "Pirate";
    default:
      return null;
  }
}

// Both Mission Boards are exactly 11 missions -- the locked curation shape
// (2 Easy / 6 Challenge / 2 Very Hard / 1 Almost Impossible) shared by the
// combat board (16.29) and the mining board (16.24, BOARD_SLOTS), and
// asserted in three separate test files. This is the unit of available work
// the pacing below is measured against.
export const MISSION_BOARD_SIZE = 11;

// FINAL (16.84), replacing 16.48's undeclared 0.5 placeholder.
//
// Derived from pacing, not picked: reaching Friendly (+4) -- the first tier
// crossing, and the only one that unlocks anything (best prices, best Station
// service tier, higher-tier contracts) -- should take **10 missions for one
// faction**, i.e. it lands inside a single board-clear with a mission to
// spare, so the unlock is felt while there is still work on that board rather
// than on its very last entry. Max standing (+10) then costs 25 missions,
// about 2.3 board-clears -- a real long-term investment.
//
// 10 rather than exactly 11 (one whole board) for a second, concrete reason:
// 4/10 is a terminating decimal and 4/11 is not. With the 4-decimal rounding
// in adjustStanding, an 11-completion run of 4/11 sums to 3.9996 and never
// crosses at all; unrounded it sums to 4.000000000000001 and crosses only by
// float luck. 0.4 crosses at exactly 4.0, every time, and displays exactly at
// the one decimal the Space Map shows.
// 16.95 (Don's direction): **ten times slower.** 100 missions to Friendly, not
// 10, so the gain is 0.05 rather than 0.5.
//
// This overturns 16.84's pacing argument rather than refining it, and
// deliberately: that reasoning measured standing against a single board-clear,
// on the assumption that Friendly was a mid-game unlock. Don's intent is the
// opposite -- standing is the long commitment track that decides which half of
// the galaxy a captain lives in, and 16.93/16.94 gave it teeth (station
// garrisons, docking, contraband access) that make picking a side consequential
// enough to deserve real investment. A thing that decides where you can dock
// should not be settled inside one afternoon's board.
//
// 5/100 = 0.05 terminates in binary exactly as 5/10 did, so 16.84's careful
// exact-crossing property survives untouched.
export const MISSIONS_TO_FRIENDLY = 100;
export const MISSION_COMPLETE_STANDING_GAIN = FRIENDLY_MIN_STANDING / MISSIONS_TO_FRIENDLY;

// Deliberately flat rather than difficulty- or reward-scaled, against 16.48's
// own "likely reward-tier-scaled" guess -- see 16.84 for the four reasons.
// The short version: two of the four mission types have no difficulty band at
// all, rewards span 60..95,820 CR across the roster so a reward-proportional
// gain would max out on one T4 contract and barely move at T1, standing was
// locked as a commitment track rather than a performance meter (16.48 rules
// that combat kills don't move it), and every penalty here is already flat --
// which is what makes the pirate tradeoff countable: one pirate job costs
// exactly 2.5 legitimate ones.
// Pirate work costs real-faction standing (16.48). Scaled with the gain in
// 16.95 rather than left at -1.0: against a 0.05 gain, a flat -1.0 would cost
// twenty legitimate missions to repay one pirate job, turning a tradeoff into
// a one-way door. Kept at 2x the mission gain, which is the ratio 16.84
// reasoned about and the thing actually worth preserving -- one pirate job
// costs two honest ones.
export const PIRATE_WORK_FACTION_PENALTY = -2 * MISSION_COMPLETE_STANDING_GAIN;
// Abandon (player-initiated, not natural failure) costs standing with that
// mission's poster (16.55) -- same magnitude as the Pirate-work penalty above,
// distinct mechanism.
export const ABANDON_STANDING_PENALTY = -2 * MISSION_COMPLETE_STANDING_GAIN;

// ---------------------------------------------------------------------------
// 16.95: destroying ships, trading, and buying forgiveness
// ---------------------------------------------------------------------------

/**
 * What destroying one ship costs you with its faction.
 *
 * Don's ruling draws a careful line: *taking missions* against a faction is
 * what moves standing meaningfully, and killing is not meant to be an
 * automatic alignment switch -- but a kill should still register. So this is
 * deliberately small next to a mission: even a T4 kill is under one mission's
 * gain, and self-defence against a stray patrol barely moves the needle.
 *
 * Scaled by hull tier so a capital kill counts four times a scout, which is
 * both intuitive and means grinding the weakest possible target is the slowest
 * way to fall out of favour rather than the fastest.
 *
 * Pacing: at an average of -0.025 a kill, sliding from Neutral to Hostile takes
 * roughly 200 destroyed ships. Frequent enough that a career pirate-hunter must
 * eventually buy their way back into pirate space -- which 16.94 notes is the
 * only place contraband sells and pirate-region ore is mined -- and rare enough
 * that it never surprises a captain who fought off one ambush.
 */
export const SHIP_KILL_STANDING_PENALTY_PER_TIER = -0.01;

export function shipKillStandingPenalty(hullTier: "T1" | "T2" | "T3" | "T4"): number {
  const tierIndex = Number(hullTier.slice(1));
  return SHIP_KILL_STANDING_PENALTY_PER_TIER * tierIndex;
}

/**
 * What ordinary trade earns you with the station's faction: 10% of a mission
 * (Don's ruling).
 *
 * Small on purpose. Hauling and mining missions already pay the same standing
 * as combat contracts, so a trader is not short of ways to build reputation --
 * this exists so that simply doing business somewhere counts for something,
 * not as a route that competes with the mission board. At 0.005 a time it
 * would take 1,000 awards to reach Friendly on trade alone, which is precisely
 * why it is safe to hand out.
 */
export const TRADE_STANDING_GAIN = MISSION_COMPLETE_STANDING_GAIN * 0.1;

/**
 * Minimum transaction value that counts as trade worth noticing.
 *
 * Without a floor, the award is farmable one unit at a time -- and with a
 * per-hour cap on top (see ui/state.ts's tradeStandingAwarded) it cannot be
 * repeated within the same market hour either. Two independent guards, because
 * a reputation system that can be ground is not a reputation system.
 */
export const TRADE_STANDING_MIN_VALUE_CR = 5_000;

/**
 * The forgiveness fine: buying your way out of a faction's bad books.
 *
 * Don's brief -- scaled to the debt, and "much more than the most expensive
 * ship". The priciest hull in the game is 375,000 CR, so the rate is set at
 * **150,000 CR per point of negative standing**:
 *
 * | Standing | Cost to clear | vs the priciest hull |
 * |---|---|---|
 * | -5 (just Hostile) | 750,000 CR | 2x |
 * | -7 | 1,050,000 CR | 2.8x |
 * | -10 (pariah) | 1,500,000 CR | 4x |
 *
 * Linear rather than punitive-curved: the debt is already proportional to how
 * badly the captain behaved, and a curve on top would make deep hostility
 * effectively permanent -- which is the outcome an amnesty exists to prevent.
 *
 * There is no decay anywhere in this system (Don's ruling): time never repays
 * anything, so this payment is the *only* route back, and it has to remain
 * payable.
 */
export const AMNESTY_CR_PER_STANDING_POINT = 150_000;

export function amnestyCostCr(standingValue: number): number {
  if (standingValue >= 0) return 0;
  return Math.round(-standingValue * AMNESTY_CR_PER_STANDING_POINT);
}

/** Clears a faction's negative standing to exactly zero. */
export function applyAmnesty(standing: FactionStanding, faction: FactionName): FactionStanding {
  if (standing[faction] >= 0) return standing;
  return { ...standing, [faction]: 0 };
}

const REAL_FACTIONS: FactionName[] = ["Crimson Concord", "Azure Compact", "Solthane Accord"];

// Completing a faction's (or the Pirates') mission raises standing with
// that poster only -- completing a Pirate mission ALSO simultaneously
// lowers standing with all three real factions (16.48's "uneasy peace"
// mechanism). Covert-ops missions (still otherwise undesigned) are
// explicitly excluded from ever calling this at all (16.55 supersedes
// 16.48's own covert-ops standing-swap bullet: "no standing effect at
// all") -- callers simply never invoke this for a covert-ops mission,
// there is no flag to check here.
export function applyMissionCompletionStanding(standing: FactionStanding, poster: FactionName | null): FactionStanding {
  if (!poster) return standing;
  let next = adjustStanding(standing, poster, MISSION_COMPLETE_STANDING_GAIN);
  if (poster === "Pirate") {
    for (const faction of REAL_FACTIONS) {
      next = adjustStanding(next, faction, PIRATE_WORK_FACTION_PENALTY);
    }
  }
  return next;
}

export function applyAbandonStanding(standing: FactionStanding, poster: FactionName | null): FactionStanding {
  if (!poster) return standing;
  return adjustStanding(standing, poster, ABANDON_STANDING_PENALTY);
}
